import { useEffect, useState, useCallback, useMemo } from "react";
import ReviewerSidebar from "../components/ReviewerSidebar";
import api from "../services/api";
import toast from "react-hot-toast";

export default function ReviewerPendingReviews() {
  const [pendingEsmps, setPendingEsmps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [selectedESMP, setSelectedESMP] = useState(null);

  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  }, []);

  const fetchPendingEsmps = useCallback(async () => {
    if (!user?.id) {
      setError("Please log in to view your assigned reviews.");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Use the secure, filtered endpoint!
      const res = await api.get("/esmp/my-esmps");

      const data = Array.isArray(res.data?.data) ? res.data.data : [];
      console.log("📥 Assigned ESMPs from backend:", data);

      // Show all in-progress items: Submitted, Pending, and Overdue.
      // Exclude only completed outcomes.
      const pending = data.filter(
        (esmp) => !["Approved", "Returned", "Rejected"].includes(esmp.status)
      );

      setPendingEsmps(pending);
    } catch (err) {
      console.error("Failed to fetch pending reviews:", err);
      const msg = err.response?.data?.message || "Failed to load your pending reviews";
      toast.error(msg);
      setError(msg);
      setPendingEsmps([]);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchPendingEsmps();
  }, [fetchPendingEsmps]);

  const handleViewDetails = (esmp) => setSelectedESMP(esmp);

  const filtered = useMemo(() => {
    if (!Array.isArray(pendingEsmps)) return [];
    return pendingEsmps.filter((e) =>
      (e?.esmp_id || "").toLowerCase().includes(search.toLowerCase()) ||
      (e?.district || "").toLowerCase().includes(search.toLowerCase()) ||
      (e?.project_name || "").toLowerCase().includes(search.toLowerCase())
    );
  }, [pendingEsmps, search]);

  return (
    <div className="flex min-h-screen bg-gray-100">
      <ReviewerSidebar />

      <main className="flex-1 p-6 space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center bg-gradient-to-r from-green-700 to-green-500 text-white p-5 rounded-xl shadow-md">
          <div>
            <h2 className="text-2xl font-bold">Pending Reviews</h2>
            <p className="text-sm mt-1">ESMPs assigned to you awaiting review</p>
          </div>
          <div className="flex gap-3">
            <input
              type="text"
              placeholder="Search ESMPs..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="px-3 py-2 rounded text-black border focus:ring-2 focus:ring-green-600 outline-none"
            />
            <button
              onClick={fetchPendingEsmps}
              className="px-4 py-2 bg-white text-green-700 rounded-lg font-semibold hover:bg-gray-100"
            >
              Refresh
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="bg-white p-6 rounded-xl shadow">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="border-l-4 border-yellow-500 pl-4">
              <p className="text-sm text-gray-500">Total Pending</p>
              <h3 className="text-3xl font-bold">{pendingEsmps.length}</h3>
            </div>
            <div className="border-l-4 border-blue-500 pl-4">
              <p className="text-sm text-gray-500">Submitted</p>
              <h3 className="text-3xl font-bold">
                {pendingEsmps.filter((e) => e.status === "Submitted").length}
              </h3>
            </div>
            <div className="border-l-4 border-purple-500 pl-4">
              <p className="text-sm text-gray-500">Pending</p>
              <h3 className="text-3xl font-bold">
                {pendingEsmps.filter((e) => e.status === "Pending").length}
              </h3>
            </div>
            <div className="border-l-4 border-red-500 pl-4">
              <p className="text-sm text-gray-500">Overdue</p>
              <h3 className="text-3xl font-bold">
                {pendingEsmps.filter((e) => e.status === "Overdue").length}
              </h3>
            </div>
          </div>
        </div>

        {/* List */}
        <div className="bg-white p-6 rounded-xl shadow">
          <h3 className="text-lg font-semibold text-green-700 mb-4">
            Pending ESMPs ({filtered.length})
          </h3>

          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
              <p className="text-gray-500 mt-4">Loading your assigned reviews...</p>
            </div>
          ) : error ? (
            <div className="text-center py-8 text-red-600">
              {error}
              <button
                onClick={fetchPendingEsmps}
                className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Try Again
              </button>
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <p className="text-lg">
                {search ? "No matching ESMPs found" : "No pending reviews assigned to you yet"}
              </p>
              <p className="mt-2 text-sm">
                New assignments will appear here automatically.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-green-50">
                    <th className="border px-3 py-2 text-left text-green-700">ESMP ID</th>
                    <th className="border px-3 py-2 text-left text-green-700">Project</th>
                    <th className="border px-3 py-2 text-left text-green-700">District</th>
                    <th className="border px-3 py-2 text-left text-green-700">Sector</th>
                    <th className="border px-3 py-2 text-left text-green-700">Status</th>
                    <th className="border px-3 py-2 text-left text-green-700">Deadline</th>
                    <th className="border px-3 py-2 text-left text-green-700">Submitted</th>
                    <th className="border px-3 py-2 text-center text-green-700">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((esmp) => (
                    <tr key={esmp.id} className="hover:bg-green-50 transition">
                      <td className="border px-3 py-2 font-medium text-blue-600">{esmp.esmp_id}</td>
                      <td className="border px-3 py-2">{esmp.project_name}</td>
                      <td className="border px-3 py-2">{esmp.district}</td>
                      <td className="border px-3 py-2">{esmp.sector || "—"}</td>
                      <td className="border px-3 py-2">
                        <span
                          className={`px-2 py-1 rounded text-xs font-semibold ${
                            esmp.status === "Submitted"
                              ? "bg-yellow-100 text-yellow-800"
                              : esmp.status === "Overdue"
                              ? "bg-red-100 text-red-800"
                              : "bg-purple-100 text-purple-800"
                          }`}
                        >
                          {esmp.status}
                        </span>
                      </td>
                      <td className="border px-3 py-2 text-sm">
                        {esmp.deadline
                          ? new Date(esmp.deadline).toLocaleDateString()
                          : "No deadline"}
                      </td>
                      <td className="border px-3 py-2 text-sm">
                        {new Date(esmp.createdAt).toLocaleDateString()}
                      </td>
                      <td className="border px-3 py-2 text-center">
                        <button
                          onClick={() => handleViewDetails(esmp)}
                          className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {/* Modal – unchanged but can be extracted later */}
      {selectedESMP && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <h3 className="text-2xl font-bold text-green-700">ESMP Details</h3>
                <button
                  onClick={() => setSelectedESMP(null)}
                  className="text-3xl text-gray-500 hover:text-gray-700"
                >
                  ×
                </button>
              </div>

              {/* ... rest of your modal content remains the same ... */}

              <div className="flex justify-end gap-4 mt-8">
                <button
                  onClick={() => setSelectedESMP(null)}
                  className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Close
                </button>
                <button
                  onClick={() => window.location.href = '/reviewer-dashboard'}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  Go to Review
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
